import React, { useEffect, useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { PRIORITY_OPTIONS } from "../supportConstants";
import styles from "./TicketFormModal.module.css";

const SUBJECT_MIN = 5;
const DESCRIPTION_MIN = 20;

const DESCRIPTION_PLACEHOLDER =
  "Descreva o problema com o máximo de detalhes possível.\n" +
  "Ex: O chatbot está agendando no horário errado quando o cliente pede às 21h. " +
  "O agendamento fica registrado às 03h no painel.";

/**
 * Modal de criação de chamado. Validação espelha o backend (subject min 5,
 * description min 20). Acessibilidade: role=dialog, focus trap, fecha com Esc,
 * foco inicial no primeiro campo, foco devolvido ao gatilho ao fechar.
 *
 * `submitting` é controlado pelo container (que faz o POST e trata 403).
 */
export default function TicketFormModal({ onClose, onSubmit, submitting }) {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("NORMAL");
  const [errors, setErrors] = useState({});

  const overlayRef = useRef(null);
  const firstFieldRef = useRef(null);
  const titleId = useId();

  // Foco inicial no primeiro campo + devolve foco ao desmontar.
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    firstFieldRef.current?.focus();
    return () => {
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, []);

  // Esc fecha; Tab faz o foco circular dentro do modal (focus trap).
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== "Tab") return;
    const focusables = overlayRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (!focusables || focusables.length === 0) return;
    const list = Array.from(focusables).filter((el) => !el.disabled);
    const first = list[0];
    const last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const validate = () => {
    const next = {};
    if (subject.trim().length < SUBJECT_MIN) {
      next.subject = `Dê um título com pelo menos ${SUBJECT_MIN} caracteres.`;
    }
    if (description.trim().length < DESCRIPTION_MIN) {
      next.description = `Descreva com pelo menos ${DESCRIPTION_MIN} caracteres para a equipe entender melhor.`;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    onSubmit({
      subject: subject.trim(),
      description: description.trim(),
      priority,
    });
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !submitting) onClose();
  };

  return (
    <div
      ref={overlayRef}
      className={styles.overlay}
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className={styles.modal}>
        <h2 id={titleId} className={styles.title}>
          Abrir um novo chamado
        </h2>
        <p className={styles.subtitle}>
          Conte o que está acontecendo — a equipe Nexus responde por aqui.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <label className={styles.field}>
            <span className={styles.label}>Assunto</span>
            <input
              ref={firstFieldRef}
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className={styles.input}
              placeholder="Resuma o problema em poucas palavras"
              aria-invalid={errors.subject ? "true" : "false"}
              aria-describedby={errors.subject ? `${titleId}-subject-err` : undefined}
            />
            {errors.subject && (
              <span id={`${titleId}-subject-err`} role="alert" className={styles.error}>
                {errors.subject}
              </span>
            )}
          </label>

          <label className={styles.field}>
            <span className={styles.label}>Descreva o problema</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={styles.textarea}
              rows={5}
              placeholder={DESCRIPTION_PLACEHOLDER}
              aria-invalid={errors.description ? "true" : "false"}
              aria-describedby={errors.description ? `${titleId}-desc-err` : undefined}
            />
            {errors.description && (
              <span id={`${titleId}-desc-err`} role="alert" className={styles.error}>
                {errors.description}
              </span>
            )}
          </label>

          <label className={styles.field}>
            <span className={styles.label}>Prioridade</span>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className={styles.select}
            >
              {PRIORITY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <div className={styles.actions}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelBtn}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button type="submit" className={styles.submitBtn} disabled={submitting}>
              {submitting ? "Abrindo…" : "Abrir chamado"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

TicketFormModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
};
